import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MapPin,
  Flame,
  Dumbbell,
  Zap,
  Heart
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

const schedule = [
  {
    day: "Monday",
    classes: [
      { time: "5:30 PM – 6:15 PM", name: "After Work Burn", emoji: "🔥", type: "cardio" },
      { time: "6:30 PM – 7:15 PM", name: "Strength & Sweat", emoji: "💪", type: "strength" }
    ]
  },
  {
    day: "Tuesday",
    classes: [
      { time: "5:30 PM – 6:15 PM", name: "Fat Loss Boot Camp", emoji: "🔥", type: "cardio" }
    ]
  },
  {
    day: "Wednesday",
    classes: [
      { time: "5:30 PM – 6:15 PM", name: "HIIT & Core", emoji: "⚡", type: "hiit" },
      { time: "6:30 PM – 7:15 PM", name: "Glutes & Abs", emoji: "🍑", type: "strength" }
    ]
  },
  {
    day: "Thursday",
    classes: [],
    off: true
  },
  {
    day: "Friday",
    optional: true,
    classes: [
      { time: "5:30 PM – 6:15 PM", name: "Friday Burnout", emoji: "🎉", type: "cardio" }
    ]
  },
  {
    day: "Saturday",
    classes: [
      { time: "5:00 PM – 5:45 PM", name: "Weekend Shred", emoji: "🔥", type: "cardio" },
      { time: "6:00 PM – 6:45 PM", name: "Strength Circuit", emoji: "💪", type: "strength" },
      { time: "7:00 PM – 7:45 PM", name: "Core & Conditioning", emoji: "⚡", type: "hiit" }
    ]
  },
  {
    day: "Sunday",
    classes: [
      { time: "5:00 PM – 5:45 PM", name: "Sweat Session", emoji: "🔥", type: "cardio" },
      { time: "6:00 PM – 6:45 PM", name: "Full Body Burn", emoji: "💪", type: "strength" },
      { time: "7:00 PM – 7:45 PM", name: "Stretch & Core", emoji: "🧘‍♂️", type: "recovery" }
    ]
  }
];

const typeColors = {
  cardio: "bg-red-500/20 border-red-500/30 text-red-500",
  strength: "bg-orange-500/20 border-orange-500/30 text-orange-500",
  hiit: "bg-yellow-500/20 border-yellow-500/30 text-yellow-500",
  recovery: "bg-green-500/20 border-green-500/30 text-green-500"
};

const typeIcons = {
  cardio: Flame,
  strength: Dumbbell,
  hiit: Zap,
  recovery: Heart
};

export default function ClassSchedule() {
  const [selectedDay, setSelectedDay] = useState(null);
  
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/pricing" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">CLASS SCHEDULE</span>
            </div>
            <Link to="/pricing">
              <Button size="sm" className="bg-primary hover:bg-primary/90">
                Join Now
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-3xl">🥊</span>
            <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tighter">
              In-House <span className="text-primary">Classes</span>
            </h1>
            <span className="text-3xl">🥊</span>
          </div>
          <p className="text-muted-foreground">
            Boxing & conditioning classes at our gym
          </p>
        </motion.div>

        {/* Pricing Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap justify-center gap-3 mb-8"
        >
          <div className="px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full text-sm">
            <span className="text-yellow-500 font-bold">Drop-In: $10</span>
          </div>
          <div className="px-4 py-2 bg-primary/20 border border-primary/30 rounded-full text-sm">
            <span className="text-primary font-bold">3-Day Weekly: $65/mo</span>
          </div>
          <div className="px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full text-sm">
            <span className="text-orange-500 font-bold">Weekend Warrior: $70/mo</span>
          </div>
          <div className="px-4 py-2 bg-accent/20 border border-accent/30 rounded-full text-sm">
            <span className="text-accent font-bold">Coach Coop Coaching: $120/mo</span>
          </div>
        </motion.div>

        {/* Legend */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-8 text-sm"
        >
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-muted-foreground">Cardio</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <span className="text-muted-foreground">Strength</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-muted-foreground">HIIT</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-muted-foreground">Recovery</span>
          </div>
        </motion.div>

        {/* Schedule Grid */}
        <div className="grid gap-4">
          {schedule.map((day, i) => {
            const isToday = day.day === today;
            const Icon = day.off ? null : Calendar;
            
            return (
              <motion.div
                key={day.day}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card className={`border-border ${isToday ? 'ring-2 ring-primary' : ''} ${day.off ? 'opacity-50' : ''}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 uppercase tracking-tight">
                        <Calendar className={`h-5 w-5 ${isToday ? 'text-primary' : 'text-muted-foreground'}`} />
                        {day.day}
                        {isToday && (
                          <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full ml-2">
                            TODAY
                          </span>
                        )}
                        {day.optional && (
                          <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
                        )}
                      </CardTitle>
                      {day.off && (
                        <span className="text-sm text-red-500 font-bold">❌ OFF</span>
                      )}
                    </div>
                  </CardHeader>
                  
                  {!day.off && (
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {day.classes.map((cls, j) => {
                          const TypeIcon = typeIcons[cls.type] || Flame;
                          return (
                            <div
                              key={j}
                              className={`flex items-center justify-between p-3 rounded-lg border ${typeColors[cls.type]}`}
                            >
                              <div className="flex items-center gap-3">
                                <TypeIcon className="h-5 w-5" />
                                <div>
                                  <div className="font-bold text-foreground">
                                    {cls.name} {cls.emoji}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-4 w-4" />
                                <span>{cls.time}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <Card className="bg-primary/10 border-primary/30 p-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="h-6 w-6 text-primary" />
              <h3 className="text-xl font-bold uppercase tracking-tight">Ready to Train?</h3>
            </div>
            <p className="text-muted-foreground mb-6">
              Join our in-house boxing classes. Pay online, train at our gym.
            </p>
            <Link to="/pricing">
              <Button size="lg" className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest">
                View Pricing & Join
              </Button>
            </Link>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
